import React, { useState, useCallback, useRef, useEffect, type ChangeEvent, type ChangeEventHandler, memo, useMemo } from "react"; import { Editor } from '@tinymce/tinymce-react';
import FloatBoxDialog from "./Plugins/FloatBoxDialog";
import { Editor as TinyMCEEditor, type Bookmark, type EditorEvent } from 'tinymce';
import { endPoint, PUBLIC_URL } from "src/AppConstants";
import { imagePickerDuck } from "components/ImagePicker/ImagePickerDuck";
import ImagePickerDialog, { imagePickerService } from "components/ImagePicker/ImagePickerDialog";
import { useDialog } from "../Modalservice/Dialogservice";
import ImageDrop from "../ImagePicker/ImageDrop";
import DOMPurify from 'dompurify';
import { useI18n } from "../lni18n";
import type { align } from "../ImagePicker/ImagePicker_types";
import type { InitOptions } from "@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor";
import { useAppDispatch } from "src/utils/ReduxHooks";
import type { Dispatch } from "redux";
import useTypedSelector from "src/utils/useTypedSelector";
import { debouncer } from "src/utils/debouncer";
import ImageAlignDialog from "../ImagePicker/ImageAlignDialog";

export type EditorProps = {
    className?: string,
    initialValue?: string,
    onChange?: ChangeEventHandler<HTMLTextAreaElement>
    onClick?: React.MouseEventHandler<HTMLDivElement>,

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onBlur?: any,

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorSettings?: InitOptions | any,
    onSetup?: (ed: TinyMCEEditor) => void,
    value?: string,
    invalid?: boolean,
    readOnly?: boolean
    onFocus?: (el: HTMLElement) => void,
    name: string,
    iframe?: boolean
};

const HtmlTextEditor = memo(function HtmlTextEditor(props: EditorProps) {

    const { user: currentUser } = useTypedSelector(state => state.login);
    const triggerOnChangeDebouncerRef = useRef(null);

    const editorWrapper = useRef<HTMLDivElement>(null);
    const fileOptions = {
        fileExtensions: "bmp,gif,jpg,png,tif,tiff,jpeg,webp",
        maxsize: "10mb",
        mimetypes: "image/jpeg,image/png,image/gif,image/tiff,image/bmp,image/webp"
    };

    const { iframe, onFocus, readOnly, invalid, initialValue, name, editorSettings, onClick, onSetup, onBlur, onChange } = props;


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dispatch: Dispatch<any> = useAppDispatch();
    const dialogPortal = useDialog();


    const [startHtml, setStartHtml] = useState<string>("");


    const [menuElementId] = useState("menu_" + Math.floor(Math.random() * 100000));
    const { languageService: t } = useI18n();

    // NOTE: to make this works with our language name I have changed file the sv_SE.js to sv.js
    //       and in that file 'tinymce.addI18n('sv_SE'...'  to 'tinymce.addI18n('sv' ...'
    const lang = t.currentLanguage.Short;

    const editorRef = useRef<TinyMCEEditor | null>(null);

    const editor = useCallback(() => {
        return editorRef.current;
    }, []);

    const triggerOnChange = useCallback((html: string) => {

        if (editorRef.current === null) return;


        const event = {
            target: {
                value: html,
                name: name,
                type: "textarea"
            },
            currentTarget: {
                value: html,
                name: name,
                type: "textarea"
            }
        } as ChangeEvent<HTMLTextAreaElement>;

        if (onChange) onChange(event);

    }, [onChange, name]);


    const handleEditorChange = useCallback((s: string, e: TinyMCEEditor) => {

        if (e.isNotDirty === true) return;
        const html = s;
        triggerOnChangeDebouncerRef.current?.debounce(html);


    }, [triggerOnChangeDebouncerRef]);


    useEffect(() => {
        if (triggerOnChangeDebouncerRef.current != null) {
            triggerOnChangeDebouncerRef.current.clear();
        }
        const d = new debouncer(triggerOnChange, 300);
        triggerOnChangeDebouncerRef.current = d;
        return () => {
            d.flush();
            triggerOnChangeDebouncerRef.current = null;
        };
    }, [triggerOnChange]);

    useEffect(() => {
        setStartHtml(initialValue || "");

    }, [initialValue]);


    const setEditor = (ed: TinyMCEEditor) => {
        editorRef.current = ed;
    }


    const handleDroppedFileReady = (id: string, bookMark: Bookmark, ed: TinyMCEEditor) => {
        if (bookMark) {
            ed.selection.moveToBookmark(bookMark);
        }

        if (id) {
            const url = endPoint.GET_IMAGE_URL(id);
            ed.insertContent(`<img src="${url}" data-id="${id}" />`)
        }
    };


    const setDropFiles = useCallback((files: File[] | FileList | null) => {
        const ed = editor();
        if (ed === null) return;

        const bookMark = ed.selection.getBookmark();


        dialogPortal({
            factory: (onSubmit, onCancel) => {
                return <ImageDrop onCancel={onCancel}
                    files={files} imagePreviewOptions={[{ name: "full", maxheight: 1200, maxwidth: 1200 }]}
                    onSubmit={onSubmit} accept={fileOptions.fileExtensions} languageService={t}
                    maxsize={fileOptions.maxsize} />
            },
            size: "xl"
        })
            .then(res => {
                if (res)
                    handleDroppedFileReady(res, bookMark, ed);
            })
            .catch(() => { });


    }, [dialogPortal, editor, fileOptions.fileExtensions, fileOptions.maxsize, t]);


    // unique id for each instance of editor
    const [editorId] = useState("id_" + (new Date()).getTime() + "_");

    const imageDialog = useCallback((ed) => {


        const imgElm: HTMLImageElement = ed.selection.getNode();

        if (!!imgElm && imgElm.nodeName.toLowerCase() === "img") {

            let align: align = "none";
            if (imgElm.classList.contains("pull-left") === true) {
                align = "left";
            }
            if (imgElm.classList.contains("pull-right") === true) {
                align = "right";
            }
            const dataId = imgElm.attributes.getNamedItem("data-id");
            const id = dataId ? dataId.value : null;
            const src = imgElm.src;

            dispatch(imagePickerDuck.setAlingData({
                modalOpen: true, imageAlign: align,
                imageId: id, url: src
            }));


            dialogPortal({
                factory: (onSubmit, onCancel) => {
                    return <ImageAlignDialog onCancel={onCancel} onSubmit={onSubmit} />
                },
                size: "xl"
            })
                .then(res => {
                    dispatch(imagePickerDuck.setAlingData(null));
                    if (res) {
                        imgElm.classList.remove('pull-right', 'pull-left');
                        if (res !== 'none') {
                            imgElm.classList.add('pull-' + res);
                        }
                    }
                    ed.focus();
                })
                .catch(() => { })
                .finally(() => {
                    dispatch(imagePickerDuck.setAlingData(null));
                    ed.focus();
                });



        }
        else {
            dispatch(imagePickerDuck.fetchImageData(currentUser.Id));
            dispatch(imagePickerDuck.setModalState(true));


            /**
             * 
             * @param {{imageId: string, url:string, imageAlign: 'none' | 'left' | 'right' }} res 
             */
            imagePickerService.onSubmit = (res) => {
                dispatch(imagePickerDuck.setModalState(false));
                if (res) {
                    let className = "";
                    if (res.imageAlign !== 'none') {
                        className = `class="pull-${res.imageAlign}"`;
                    }

                    ed.focus();
                    if (res.imageId) {
                        ed.insertContent(`<img ${className} src="${res.url}" data-id="${res.imageId}" />`)
                    }
                    else {
                        ed.insertContent(`<img ${className} src="${res.url}" />`)
                    }
                }
                ed.focus();
            };

            dialogPortal({
                factory: (onSubmit, onCancel) => {
                    return <ImagePickerDialog onCancel={onCancel} onSubmit={onSubmit} />
                },
                size: "xl"
            })
                .then(res => {
                    imagePickerService.onSubmit(res);
                })
                .catch(() => { })
                .finally(() => {
                    dispatch(imagePickerDuck.setAlingData(null));
                    ed.focus();

                })
        }


    }, [dispatch, currentUser.Id]);

    const floatBoxDialog = (ed: TinyMCEEditor) => {

        const floatdata = { align: "none", style: "Facts", width: 400, canDelete: false };

        const box = ed.dom.getParent(ed.selection.getNode(), "div.box");
        if (box != null) {
            floatdata.width = parseInt(ed.dom.getAttrib(box, "data-width"), 10);
            floatdata.align = ed.dom.getAttrib(box, "data-align");
            floatdata.style = ed.dom.getAttrib(box, "data-type");
            floatdata.canDelete = true;
        }

        const p = dialogPortal({
            factory: (onSubmit) => {
                return <FloatBoxDialog floatCallback={onSubmit} boxState={floatdata} />
            },
            size: "lg"
        });

        p.then(res => {
            if (res) {
                const css = `box ${res.style} ${res.align}box w${res.width}`;
                if (box) {

                    if (res.delete) {
                        ed.dom.remove(box, false)
                    }
                    else {
                        ed.dom.setAttrib(box, "data-type", res.style);
                        ed.dom.setAttrib(box, "data-align", res.align);
                        ed.dom.setAttrib(box, "data-width", res.width);
                        ed.dom.setAttrib(box, "class", css);
                    }
                }
                else {
                    const html = `<div class='${css}' data-type='${res.style}' data-align='${res.align}'`
                        + ` data-width='${res.width}'><p>&#160;</p></div>`;
                    ed.insertContent(html);
                }
            }
            ed.focus();
        }).catch(() => { });


    };



    const handleEditorBlur = useCallback((ev) => {

        //const html = e.getContent();
        //triggerOnChange(html);
        if (onBlur) onBlur(ev);

    }, [onBlur]);






    const onPaste = async () => {

        try {
            const clipboardItems = await navigator.clipboard.read();
            for (const clipboardItem of clipboardItems) {

                const type = clipboardItem.types.find(t => t.indexOf("image") > -1)
                if (type) {
                    const blob = await clipboardItem.getType(type);
                    const ext = type.split("/")?.[1] || "jpg";
                    const f = new File([blob], `clip.${ext}`, { type })
                    setDropFiles([f]);
                    return;
                }
            }
        } catch (err: unknown) {
            const e = err as { name: string, message: string };
            if (e) {
                console.error(e.name, e.message);
            }
            else {
                console.error(e);
            }
        }
    }

    const uploadImage = (ev: EditorEvent<DragEvent>) => {
        const files = ev.dataTransfer?.files;
        if (files) {
            setDropFiles(files);
            ev.preventDefault();
            ev.stopPropagation();
            return false;
        }
    }


    const hostAndPort = window.location.hostname + (window.location.port ? (":" + window.location.port) : "");
    const tinyMceUrl = `${window.location.protocol}//${hostAndPort}/tinymce`;


    const settings: InitOptions = useMemo(() => {



        let value: InitOptions = {
            inline: false,

            suffix: '.min',
            branding: false,
            promotion: false,
            //icons: "lnauthoricons",
            language: lang,
            relative_urls: false,
            remove_script_host: false,
            // document_base_url: APP_URL,
            theme: editorSettings && (editorSettings.theme || 'silver'),
            base_url: `${tinyMceUrl}`,
            contextmenu: "imagealign,floatbox",
            plugins: (editorSettings?.plugins) || "media visualblocks code link autolink table charmap lists",
            toolbar: (editorSettings?.toolbar) || "undo redo | styles | table | bold italic removeformat | align | image floatbox media| bullist numlist outdent indent  |  link code charmap ",
            menubar: (editorSettings?.menubar) ?? false, // || "edit insert table format view tools",
            formats: {
                removeformat: [
                    {
                        selector: 'b,strong,em,i,font,u,strike,s,sub,sup,dfn,code,samp,kbd,var,cite,mark,q,del,ins,small',
                        remove: 'all',
                        split: true,
                        block_expand: true,
                        expand: false,
                        deep: true
                    },
                    {
                        selector: 'h1,h2,h3,h4,h5',
                        remove: 'all',
                        split: true,
                        block_expand: true,
                        expand: false,
                        deep: true
                    },
                    { selector: 'span', attributes: ['style', 'class'], remove: 'empty', split: true, expand: false, deep: true },
                    { selector: 'img', attributes: ['style', 'class'], remove: 'none', split: true, expand: false, deep: true },
                    { selector: '*', attributes: ['style', 'class'], split: false, expand: false, deep: true }
                ]
            },
            toolbar_items_size: "small",
            browser_spellcheck: true,
            fixed_toolbar_container: !editorSettings?.toolbar ? "nope" : "#" + menuElementId,
            skin_url: `${PUBLIC_URL}/tinymce/skins/ui/oxide`,
            paste_as_text: true,
            valid_elements: (editorSettings?.valid_elements) || "@[contenteditable|id|style|data*],-h1,-h2,-h3,-h4,-h5,-strong/b,-em/i,#p," +
                "-div[class],br,table[class],tr[class],td[class],thead[class],tbody[class],th[class],tfoot[class],-span" +
                ",source,video[controls|width|height],iframe[width|height|src|frameborder|allowfullscreen],-ol,-ul,-li,dl,dt,hr,del/strike,ins,rp,rt,ruby,-small,sub,sup,-code,-blockquote" +
                ",-a[href|target=_blank],img[class|src|alt|title|width|height|align|data-id]",

            media_live_embeds: true,

            object_resizing: "img",
            table_default_attributes: {
                'class': 'table'
            },
            table_class_list: [
                { title: 'none', value: '' },
                { title: 'default', value: 'table' },
                { title: 'striped', value: 'table table-striped' },
                { title: 'condensed', value: 'table table-small' },
                { title: 'condensed_striped', value: 'table table-condensed table-striped' },
                { title: 'border', value: 'table table-bordered' }
            ],

            table_row_class_list: [
                { title: 'none', value: '' },
                { title: 'notification.displaytype.success', value: 'table-success' },
                { title: 'active', value: 'table-active' },
                { title: 'notification.displaytype.danger', value: 'table-danger' },
                { title: 'notification.displaytype.warning', value: 'table-warning' }
            ],

            table_cell_class_list: [
                { title: 'none', value: '' },
                { title: 'notification.displaytype.success', value: 'table-success' },
                { title: 'active', value: 'table-active' },
                { title: 'notification.displaytype.danger', value: 'table-danger' },
                { title: 'notification.displaytype.warning', value: 'table-warning' }
            ],

            //table_cell_class_list: [{ title: 'None', value: '' } ],

            table_row_advtab: true,
            table_cell_advtab: false,
            table_advtab: false,

            setup: (ed: TinyMCEEditor) => {

                if (editorSettings?.customSetup) {
                    const f = editorSettings.customSetup as (e: TinyMCEEditor) => void;
                    if (f) f(ed);
                }

                ed.on('focus', (e) => {
                    if (onFocus) onFocus(e.target.bodyElement);
                });


                if (onSetup) {
                    onSetup(ed);
                }
                ed.on("drop", (e) => {
                    uploadImage(e);
                });
                ed.on("paste", () => onPaste());

                ed.ui.registry.addButton('image', {
                    icon: 'image',
                    tooltip: 'Insert/edit image',
                    onAction: () => imageDialog(ed)

                    // stateSelector: 'img:not([data-mce-object],[data-mce-placeholder])'
                });


                ed.ui.registry.addButton('floatbox', {
                    icon: 'floatbox',
                    tooltip: 'Box',
                    onAction: () => floatBoxDialog(ed),
                });



                ed.ui.registry.addContextMenu('imagealign', {

                    update: (element: Element) => {
                        return !(element as HTMLImageElement).src ? ''
                            : [
                                {
                                    icon: 'noalign', type: "item", text: "", onAction: () => setImageClass(element, null)
                                },
                                {
                                    icon: "block", type: "item", text: "", onAction: () => setImageClass(element, "block")
                                },
                                {
                                    icon: "leftalign", type: "item", text: "", onAction: () => setImageClass(element, "left")
                                },
                                {
                                    icon: "rightalign", type: "item", text: "", onAction: () => setImageClass(element, "right")
                                }
                            ]
                    }
                });

                setEditor(ed);

            }
        }

        function setImageClass(element: Element, className: string | null): void {
            const img = element as HTMLImageElement;
            img.classList.remove("float-left", "float-right");
            switch (className) {
                case null:
                    img.classList.remove("d-block", "pull-left", "pull-right");
                    break;
                case "block":
                    img.classList.remove("pull-left", "pull-right");
                    img.classList.add("d-block");
                    break;
                case "left":
                    img.classList.remove("d-block", "pull-left", "pull-right");
                    img.classList.add("pull-left");
                    break;

                case "right":
                    img.classList.remove("d-block", "pull-left", "pull-right");
                    img.classList.add("pull-right");
                    break;
            }
        }


        const tableSettings = ["table_class_list", "table_row_class_list", "table_cell_class_list"];

        tableSettings.forEach(key => {
            const d = value[key] as Record<string, { title: string, value: string }>[];
            d.forEach((e) => {
                e.title = t.getText(e.title);
            });
        });

        if (editorSettings) {
            value = { ...value, ...editorSettings }
        }

        // inline not supported on mobile
        value.inline = !iframe;

        return value;
    }, [lang, editorSettings, tinyMceUrl, menuElementId, iframe, onSetup, onFocus, uploadImage, onPaste, imageDialog, floatBoxDialog, t]);






    if (readOnly) return <div className="" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(initialValue || "<div>&nbsp;</div>") }}></div>



    return (
        <> <div onClick={onClick} ref={editorWrapper} className={`${invalid ? "invalid" : ""} ${props.className}  position-relative`}>
            <div className="outerMceMenuWrapper">
                <div className="innerMceMenuWrapper" id={menuElementId}></div>
            </div>
            <Editor
                licenseKey="gpl"
                onEditorChange={handleEditorChange}
                onBlur={handleEditorBlur}
                initialValue={startHtml}
                id={editorId + name}
                tinymceScriptSrc={`${tinyMceUrl}/tinymce.min.js`}
                init={settings}
            />

        </div>


        </>
    )

});




export default HtmlTextEditor;


